import React from "react";

interface DonutChartProps {
  data: {
    label: string;
    value: number;
    color: string;
  }[];
  size?: number;
  innerRadius?: number;
  outerRadius?: number;
  children?: React.ReactNode;
  className?: string;
}

export default function DonutChart(props: DonutChartProps) {
  const {
    data,
    children,
    size = 200,
    innerRadius = 60,
    outerRadius = 100,
    className,
  } = props;
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const isFullCircle = data.some((item) => item.value === totalValue);
  const center = size / 2;

  if (isFullCircle) {
    const circlePathData = `
      M ${center + outerRadius} ${center}
      A ${outerRadius} ${outerRadius} 0 1 1 ${center - outerRadius} ${center}
      A ${outerRadius} ${outerRadius} 0 1 1 ${center + outerRadius} ${center}
      M ${center + innerRadius} ${center}
      A ${innerRadius} ${innerRadius} 0 1 0 ${center - innerRadius} ${center}
      A ${innerRadius} ${innerRadius} 0 1 0 ${center + innerRadius} ${center}
      Z
    `;

    return (
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className={className}
        preserveAspectRatio="xMinYMin meet"
      >
        <g>
          <path
            d={circlePathData}
            fill={data[0].color}
            stroke="white"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </g>
        {children}
      </svg>
    );
  }

  // Helper function to calculate path for each slice
  const calculateArcPath = (startAngle: number, endAngle: number) => {
    // calculate points on the outer arc
    const startOuterX = center + outerRadius * Math.cos(startAngle);
    const startOuterY = center + outerRadius * Math.sin(startAngle);
    const endOuterX = center + outerRadius * Math.cos(endAngle);
    const endOuterY = center + outerRadius * Math.sin(endAngle);

    // calculate points on the inner arc
    const startInnerX = center + innerRadius * Math.cos(endAngle);
    const startInnerY = center + innerRadius * Math.sin(endAngle);
    const endInnerX = center + innerRadius * Math.cos(startAngle);
    const endInnerY = center + innerRadius * Math.sin(startAngle);

    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    return `
      M ${startOuterX} ${startOuterY}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuterX} ${endOuterY}
      L ${startInnerX} ${startInnerY}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${endInnerX} ${endInnerY}
      Z
    `;
  };

  let cumulativeValue = 0;

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      preserveAspectRatio="xMinYMin meet"
    >
      {data.map((slice, index) => {
        const startAngle =
          (cumulativeValue / totalValue) * 2 * Math.PI - Math.PI / 2;

        cumulativeValue += slice.value;
        const endAngle =
          (cumulativeValue / totalValue) * 2 * Math.PI - Math.PI / 2;

        const pathData = calculateArcPath(startAngle, endAngle);

        return (
          <g key={index}>
            <path
              d={pathData}
              fill={slice.color}
              stroke="white"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
      {children}
    </svg>
  );
}
